/* eslint-disable react-hooks/exhaustive-deps */
// @flow
import * as React from "react";
import "./SwimLane.scss";
import useEmblaCarousel from "embla-carousel-react";
import {useTranslation} from "react-i18next";

type SwimLaneProps = {
    children: any;
    id: ?string,
    setAllowsClick: boolean => void
};

const SwimLane = (props: SwimLaneProps) => {
    const [viewportRef, embla] = useEmblaCarousel({
        /* "auto" groups the slides by what actually fits into the
           viewport, so there is always a reachable end snap. The former
           fixed group size (2) collapsed the scroll range whenever the
           lane held exactly two slides that did not fit side by side —
           the last card stayed cut off on mobile (#56597). */
        slidesToScroll: "auto",
        dragFree: true,
        loop: false,
        align: "start",
        containScroll: "trimSnaps",
    });

    const {t} = useTranslation();

    const deviceSupportsTouch = "ontouchstart" in (document.documentElement || {});

    const scrollPrev = React.useCallback(() => embla && embla.scrollPrev(), [embla]);
    const scrollNext = React.useCallback(() => embla && embla.scrollNext(), [embla]);

    const setAllowsClick = React.useCallback((allowed: boolean) => {
        if (typeof props.setAllowsClick === "function") props.setAllowsClick(allowed);
    }, [props.setAllowsClick]);

    React.useEffect(() => {
        if (!embla) return undefined;
        /* Embla 8 swallows the click that ends a drag by itself; the
           allowsClick flag additionally keeps the consumers' own click
           handlers (car tiles, model dropdown) quiet while the lane is
           still moving. Size and slide changes are observed by Embla
           (ResizeObserver / MutationObserver), so the former manual
           window "resize" nudge (#42330) is no longer needed. */
        const onScroll = () => setAllowsClick(false);
        const onSettle = () => setAllowsClick(true);
        embla.on("scroll", onScroll);
        embla.on("settle", onSettle);
        setAllowsClick(true);
        return () => {
            embla.off("scroll", onScroll);
            embla.off("settle", onSettle);
        };
    }, [embla, setAllowsClick]);

    return <div id={props.id} className="swimLane">
        <div ref={viewportRef} className="embla">
            <div className="embla__container">
                {props.children}
            </div>
            {!deviceSupportsTouch && <React.Fragment>
                <button aria-label={t("navigation.prevSlide")} className={"carouselBtn"} type={"button"} onClick={scrollPrev}/>
                <button aria-label={t("navigation.nextSlide")} className={"carouselBtn"} type={"button"} onClick={scrollNext}/>
            </React.Fragment>}
        </div>
    </div>;
};

export default SwimLane;
